import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PatchForm from "./components/PatchForm";
import PatchDetails from "../PatchDetails";
import { useLanguage } from "../../contexts/LanguageContext";
import { toast } from "react-toastify";
import { MdRestartAlt } from "react-icons/md";
import { BsArrowLeftRight } from "react-icons/bs";
import Loading from "../../components/Loading";
import { useAuth } from "../../contexts/AuthContext";

const API_URL = process.env.REACT_APP_API_URL;

const UpdatePatch = () => {
  const { patchNo } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patch, setPatch] = useState(null);
  const [newPatch, isNewPatch] = useState(false);
  const { language } = useLanguage();
  const [hasChange, setHasChange] = useState(false);
  const [leftWidth, setLeftWidth] = useState(50);
  const [loading, setLoading] = useState(false);

  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = leftWidth;

    const onMouseMove = (e) => {
      const delta = e.clientX - startX;
      const newWidth =
        (((startWidth / 100) * window.innerWidth + delta) / window.innerWidth) *
        100;

      if (newWidth > 1 && newWidth < 99) {
        setLeftWidth(newWidth);
      }
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChange) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChange]);

  useEffect(() => {
    const fetchPatchnote = async () => {
      try {
        const res = await fetch(`${API_URL}/${language}/patchnote/${patchNo}`, {
            headers: { 
                Authorization: `Bearer ${
                    JSON.parse(localStorage.getItem("user"))?.token
                }` 
            }
        });
        const data = await res.json();
        if (!data?.message) {
          if (data.title === "") {
            const fallbackLang = language === "tr" ? "en" : "tr";
            const resFallback = await fetch(
              `${API_URL}/${fallbackLang}/patchnote/${patchNo}`, {
                    headers: { 
                        Authorization: `Bearer ${
                            user.token
                        }` 
                    },
                }
            );
            
            const fallbackData = await resFallback.json();
            const newPatch = {
              ...fallbackData,
              _id: data._id,
            };
            setPatch(newPatch);
          } else {
            setPatch(data);
          }
        } else {
          const fallbackLang = language === "tr" ? "en" : "tr";
          const resFallback = await fetch(
            `${API_URL}/${fallbackLang}/patchnote/${patchNo}`, {
                headers: { 
                    Authorization: `Bearer ${
                        JSON.parse(localStorage.getItem("user"))?.token
                    }` 
                }
            });
          const data = await res.json();
          setPatch(data);
          const fallbackData = await resFallback.json();
          isNewPatch(true);
          setPatch({
            yamaVersiyonu: fallbackData.yamaVersiyonu,
            title: "",
            subtitle: "",
            summary: "",
            tarih: new Date().toISOString(),
            image: "",
            sections: [
              {
                title: "",
                content: "",
                images: [],
                table: { columns: [], rows: [] },
                thumbnail: [],
                bossUpdate: false,
              },
            ],
          });
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (patchNo) fetchPatchnote();
  }, [patchNo, language]);

  const savePatch = async (newPatch) => {
    try {
      const res = await fetch(`${API_URL}/${language}/addpatch?patchNo=${newPatch.yamaVersiyonu}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${JSON.parse(localStorage.getItem("token")).token}`,
        },
        body: JSON.stringify(newPatch),
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.error || "Yama kaydedilemedi: " + data.message);
        return;
      }

      toast.success(
        `${
          newPatch.yamaVersiyonu
        } No'lu yama ${language.toUpperCase()} dilinde eklendi.`
      );
      setHasChange(false);
    } catch (err) {
      toast.error("Sunucu Hatası");
    }
  };

  const updatePatch = async (updated) => {
    try {
      const formData = new FormData();

      const dataURLtoBlob = (dataurl) => {
        const arr = dataurl.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new Blob([u8arr], { type: mime });
      };

      const newSections = await Promise.all(
        updated.sections.map(async (s, i) => {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = s.content;

          const imgTags = tempDiv.querySelectorAll("img");

          for (let j = 0; j < imgTags.length; j++) {
            const img = imgTags[j];
            if (img.src.startsWith("blob")) {
              const res = await fetch(img.src);
              const blob = await res.blob();
              const mime = blob.type.split("/")[1].replace("+xml", "");
              const fieldName = `editorImage-${i}-${j}.${mime}`;
              formData.append(`${fieldName}`, blob, `editor-${i}-${j}.${mime}`);
              img.dataset.placeholder = fieldName;
              img.src = fieldName;
            } else if (img.src.startsWith("data:image/")) {
              const blob = dataURLtoBlob(img.src);
              const mime = blob.type.split("/")[1].replace("+xml", "");
              const fieldName = `editorImage-${i}-${j}.${mime}`;
              formData.append(`${fieldName}`, blob, `editor-${i}-${j}.${mime}`);
              img.dataset.placeholder = fieldName;
              img.src = fieldName;
            } else if (img.src.startsWith("http")) {
              const res = await fetch(img.src);
              const blob = await res.blob();
              const mime = blob.type.split("/")[1].replace("+xml", "");
              const fieldName = `editorImage-${i}-${j}.${mime}`;
              formData.append(`${fieldName}`, blob, `editor-${i}-${j}.${mime}`);
              img.dataset.placeholder = fieldName;
              img.src = fieldName;
            }
          }

          return {
            ...s,
            content: tempDiv.innerHTML,
          };
        })
      );

      formData.append(
        "patch",
        JSON.stringify({
          _id: updated._id,
          yamaVersiyonu: updated.yamaVersiyonu,
          title: updated.title,
          subtitle: updated.subtitle,
          tarih: updated.tarih,
          summary: updated.summary,
          image: updated.image,
          sections: newSections.map((s) => ({
            id: s.id,
            title: s.title,
            content: s.content,
            table: s.table,
            thumbnail: s.thumbnail,
            images: s.images,
            bossUpdate: s.bossUpdate,
          })),
        })
      );

      if (updated.imageFile) formData.append("image", updated.imageFile);

      updated.sections.forEach((s, i) => {
        if (s.imageFile) formData.append(`sectionImage-${i}`, s.imageFile);
        if (s.thumbnailFile)
          formData.append(`sectionThumbnail-${i}`, s.thumbnailFile);
      });

      const res = await fetch(
        `${API_URL}/${language}/updatepatch/${updated._id}?patchNo=${updated.yamaVersiyonu}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${
              JSON.parse(localStorage.getItem("user"))?.token
            }`,
          },
          body: formData,
        }
      );

      const data = await res.json();
      setLoading(false)
      if (!res.ok) return toast.error(data.error || "Yama güncellenemedi");

      toast.success(`${updated.yamaVersiyonu} No'lu yama güncellendi.`);
      setHasChange(false);
    } catch (err) {
      toast.error("Sunucu Hatası: " + err);
    }
  };

  const deletePatch = async (toDelete) => {
    try {
      const res = await fetch(
        `${API_URL}/${language}/deletepatch/${toDelete._id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${
              JSON.parse(localStorage.getItem("user"))?.token
            }`,
          },
        }
      );
      const data = await res.json();

      if (!res.ok) {
        toast.error(data.error || "Yama silinemedi: " + data.message);
        return;
      }

      navigate("/dashboard");
      toast.success(
        `${
          toDelete.yamaVersiyonu
        } No'lu yama ${language.toUpperCase()} silindi.`
      );
      setHasChange(false);
    } catch (err) {
      toast.error("Sunucu Hatası");
    }
  };

  if (!patch) return <Loading />;

  return (
    <>
      {/* <div className="relative top-48 left-0 right-0 btn btn-success z-40" onClick={() => window.open(`/patch/${patch.yamaVersiyonu}`, "_blank")}> Yama Notuna Git</div> */}
      <div className="sticky z-40 p-2 gap-2 top-0 grid grid-cols-10 grid-rows-auto opacity-0 transition-opacity duration-300 hover:opacity-100 bg-gray-700/30 mr-5">
        {patch.sections?.map((sec, i) => (
          <div
            key={i}
            className="bg-gray-600 m-1 rounded-md text-md text-center cursor-pointer hover:bg-gray-500"
            onClick={() => {
              setTimeout(() => {
                const el = document.getElementById(`${sec.id}`);
                const el2 = document.getElementById(`sec${sec.id}`);
                if (el)
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                if (el2)
                  el2.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 50);
            }}
          >
            <span>{i + 1 + ". Bölüm "}</span>
          </div>
        ))}
      </div>

      <div className="flex h-screen overflow-hidden">
        <div
          style={{ width: `${leftWidth}%`, ...(leftWidth == 50) && {transition: "width 2s cubic-bezier(0.4, 0, 0.2, 1)"}}}
          className="overflow-y-auto border-gray-300"
        >
          <PatchForm
            setHasChange={setHasChange}
            patch={patch}
            setPatch={setPatch}
            onSave={!newPatch ? updatePatch : savePatch}
            onDelete={deletePatch}
            loading={loading}
            setLoading={setLoading}
          />
        </div>

        <div
          onMouseDown={handleMouseDown}
          className="relative gap-6 w-12 flex flex-col justify-center items-center cursor-col-resize group"
        >
          <div className="absolute inset-y-0 w-1 bg-gray-400 opacity-30 group-hover:opacity-60 transition-opacity duration-150 rounded" />
          <div 
            className="relative z-30 w-14 h-14 font-medium text-sm cursor-pointer text-white bg-success rounded-full shadow-lg flex items-center justify-center group-hover:scale-105 transform transition-transform duration-150"
            onClick={() => window.open(`/patch/${patch.yamaVersiyonu}`, "_blank")}
            >
            Önizle
          </div>
          <div className="relative z-30 w-12 h-12 bg-white/80 rounded-full shadow-lg flex items-center justify-center group-hover:scale-105 transform transition-transform duration-150">
            <BsArrowLeftRight className="text-gray-600 text-2xl" />
          </div>
          <div
            className="cursor-pointer relative z-30 w-12 h-12 bg-white/80 rounded-full shadow-lg flex items-center justify-center group-hover:scale-105 transform transition-transform duration-150"
            onClick={() => setLeftWidth(50)}
          >
            <MdRestartAlt className="text-red-500 text-2xl" />
          </div>
        </div>

        <div
          style={{ width: `${100 - leftWidth}%` }}
          className="overflow-y-auto"
        >
          <PatchDetails initialPatch={patch} />
        </div>
      </div>
    </>
  );
};

export default UpdatePatch;